import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Menu, MenuItem } from "@mui/material";

const PURPLE = "#864797";
const GREY = "#5b5570";
const OPEN_BG = "rgba(134,71,151,.14)";

/**
//    * @component  ActionMenu
//    * @description E5 design "⋯" action menu (zone card, Zone Configuration header, network row).
//    * Clicks never reach the parent (card / row), so opening the menu doesn't navigate or expand.
//    * @returns ⋯ button with its menu
//    */
function ActionMenu(props) {
  const { items, ariaLabel, sx } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const toggle = (e) => {
    stop(e);
    setAnchorEl(open ? null : e.currentTarget);
  };

  const close = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setAnchorEl(null);
  };

  const pick = (item) => (e) => {
    stop(e);
    setAnchorEl(null);
    item.onClick();
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        onClick={toggle}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          width: 26,
          height: 26,
          border: "none",
          borderRadius: "6px",
          backgroundColor: open ? OPEN_BG : "transparent",
          color: open ? PURPLE : GREY,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "3px",
          p: 0,
          "&:hover": { backgroundColor: OPEN_BG, color: PURPLE },
          ...sx,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            component="span"
            sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "currentColor", display: "block" }}
          />
        ))}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: "4px",
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(16,24,40,.16), 0 0 0 1px rgba(16,24,40,.06)",
            minWidth: 160,
          },
        }}
        MenuListProps={{ sx: { p: "6px" } }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.label}
            onClick={pick(item)}
            sx={{
              borderRadius: "6px",
              p: "8px 12px",
              fontSize: 12,
              color: item.destructive ? "#b42318" : "#202020",
              textTransform: "none",
              "&:hover": { backgroundColor: item.destructive ? "#fef3f2" : "#f4eef7" },
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

ActionMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      destructive: PropTypes.bool,
    })
  ).isRequired,
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};

ActionMenu.defaultProps = {
  ariaLabel: "Actions",
  sx: {},
};

export default ActionMenu;
